import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    CircularProgress,
    Alert
} from '@mui/material';
import { cachedGet } from 'Utils/cachedGet';

const PermissionsPage = () => {
    const [permissions, setPermissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const user = useSelector(state => state.auth.user || {});
    const userPermissions = user.permissions || [];

    useEffect(() => {
        const fetchPermissions = async () => {
            try {
                setLoading(true);
                const response = await cachedGet('/permissions');
                setPermissions(response.data.permissions || []);
                setError(null);
            } catch (err) {
                console.error('Error fetching permissions:', err);
                setError('Failed to load permissions');
            } finally {
                setLoading(false);
            }
        };

        fetchPermissions();
    }, []);

    const hasPermission = (permissionName) => {
        return userPermissions.includes(permissionName);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Typography variant="h4" gutterBottom>
                All Permissions
            </Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom>
                Total permissions: {permissions.length}
            </Typography>
            
            <Paper sx={{ mt: 3 }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Permission Name</strong></TableCell>
                                <TableCell><strong>Description</strong></TableCell>
                                <TableCell><strong>Your Access</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {permissions.map((permission) => (
                                <TableRow key={permission.id}>
                                    <TableCell>
                                        <Typography variant="body2" fontFamily="monospace">
                                            {permission.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {permission.description || 'No description available'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {hasPermission(permission.name) ? (
                                            <Chip 
                                                label="Granted" 
                                                color="success" 
                                                size="small"
                                            />
                                        ) : (
                                            <Chip 
                                                label="Not Granted" 
                                                color="default" 
                                                size="small"
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
};

export default PermissionsPage; 